import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiUser, FiCalendar, FiEye, FiTrash2, FiExternalLink, FiSearch, FiFilter } = FiIcons;

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = () => {
    const storedMessages = JSON.parse(localStorage.getItem('contact_messages') || '[]');
    setMessages(storedMessages.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
  };

  const markAsRead = (messageId) => {
    const updatedMessages = messages.map(msg =>
      msg.id === messageId ? { ...msg, read: true } : msg
    );
    setMessages(updatedMessages);
    localStorage.setItem('contact_messages', JSON.stringify(updatedMessages));
  };

  const deleteMessage = (messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      const updatedMessages = messages.filter(msg => msg.id !== messageId);
      setMessages(updatedMessages);
      localStorage.setItem('contact_messages', JSON.stringify(updatedMessages));
      setSelectedMessage(null);
    }
  };

  const filteredMessages = messages.filter(msg => {
    const matchesFilter = filter === 'all' || 
      (filter === 'unread' && !msg.read) || 
      (filter === 'read' && msg.read);
    
    const matchesSearch = searchQuery === '' || 
      msg.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      msg.workEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      msg.projectDescription?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const unreadCount = messages.filter(msg => !msg.read).length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Messages</h1>
          <p className="text-gray-600 text-sm">
            {messages.length} total • {unreadCount} unread
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="all">All Messages</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Messages List */}
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">
              Messages ({filteredMessages.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {filteredMessages.length > 0 ? (
              filteredMessages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedMessage?.id === message.id ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                  } ${!message.read ? 'bg-blue-25' : ''}`}
                  onClick={() => {
                    setSelectedMessage(message);
                    if (!message.read) {
                      markAsRead(message.id);
                    }
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className={`text-sm font-medium truncate ${
                          !message.read ? 'text-gray-900' : 'text-gray-700'
                        }`}>
                          {message.fullName}
                        </h3>
                        {!message.read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mb-1 truncate">{message.workEmail}</p>
                      <p className="text-sm text-gray-600 truncate">{message.projectDescription}</p>
                      <div className="flex items-center space-x-3 mt-2">
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <SafeIcon icon={FiCalendar} className="h-3 w-3" />
                          <span>{new Date(message.timestamp).toLocaleDateString()}</span>
                        </div>
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded-full border border-blue-200">
                          {message.projectType}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="p-8 text-center">
                <SafeIcon icon={FiMail} className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No messages found</p>
                <p className="text-gray-500 text-sm">Messages will appear here when visitors contact you</p>
              </div>
            )}
          </div>
        </div>

        {/* Message Detail */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
          {selectedMessage ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Message Details</h2>
                <button
                  onClick={() => deleteMessage(selectedMessage.id)}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete message"
                >
                  <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From</label>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiUser} className="h-4 w-4 text-gray-500" />
                    <span className="text-gray-900">{selectedMessage.fullName}</span>
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <SafeIcon icon={FiMail} className="h-4 w-4 text-gray-500" />
                    <a
                      href={`mailto:${selectedMessage.workEmail}`}
                      className="text-blue-600 hover:text-blue-800 text-sm transition-colors"
                    >
                      {selectedMessage.workEmail}
                    </a>
                    <SafeIcon icon={FiExternalLink} className="h-3 w-3 text-gray-400" />
                  </div>
                </div>

                {selectedMessage.companyName && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                    <p className="text-gray-900">{selectedMessage.companyName}</p>
                  </div>
                )}

                {selectedMessage.phoneNumber && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <p className="text-gray-900">{selectedMessage.phoneNumber}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Type</label>
                  <span className="inline-block bg-blue-100 text-blue-600 text-sm font-medium px-3 py-1 rounded-full border border-blue-200">
                    {selectedMessage.projectType}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Description</label>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-gray-900 leading-relaxed text-sm">{selectedMessage.projectDescription}</p>
                  </div>
                </div>

                {selectedMessage.hearAboutUs && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">How they heard about us</label>
                    <p className="text-gray-900 text-sm">{selectedMessage.hearAboutUs}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Received</label>
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiCalendar} className="h-4 w-4 text-gray-500" />
                    <span className="text-gray-900 text-sm">
                      {new Date(selectedMessage.timestamp).toLocaleDateString()} at{' '}
                      {new Date(selectedMessage.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                </div>

                <div className="pt-4">
                  <a
                    href={`mailto:${selectedMessage.workEmail}?subject=Re: Your inquiry to aiximo.com&body=Hi ${selectedMessage.fullName},%0D%0A%0D%0AThank you for your interest in aiximo.com...`}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiMail} className="h-4 w-4" />
                    <span>Reply via Email</span>
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center">
              <SafeIcon icon={FiMail} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Select a message</p>
              <p className="text-gray-500">Choose a message from the list to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactMessages;